import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useTaal } from "@/contexts/TaalContext"


/** De schermlezertekst van de sluitknop, in de taal van de app (Frank, 18 okt 2026). */
function SluitTekst() {
  const { t } = useTaal()
  return <span className="sr-only">{t("Sluiten")}</span>
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Phone first. w-full left every dialog touching both edges of a 390
        // point screen; nothing but the settings dialog had a height limit, so
        // a tall one ran off the bottom with no way to reach its buttons; and
        // p-6 spends 96 of those 390 points on padding alone.
        // A caller's own max-w, max-h or padding still wins, because cn runs
        // tailwind-merge and the caller's className comes last.
        // GL225-2 (Frank, 1 nov 2026): op de telefoon via de verbinding staat bovenaan de vaste balk "Draait op je
        // Mac" (TunnelBanner zet --veilig-boven). Het midden en de hoogte houden die ruimte vrij, zodat de kop van
        // een venster nooit onder de balk of de klokzone valt.
        // Glass, brand guide section 05. A dialog is the one thing in the
        // product that genuinely floats above everything else, so it is the
        // surface the effect was drawn for. `glass-panel` replaces the flat
        // background rather than sitting on top of it: two backgrounds would
        // leave the blur with nothing to blur.
        "glass-panel fixed left-[50%] top-[calc(50%+var(--veilig-boven)/2)] z-50 grid w-[calc(100%-2rem)] sm:w-full max-w-lg max-h-[calc(100dvh-2rem-var(--veilig-boven))] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 p-4 sm:p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <SluitTekst />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
